<script setup lang="ts">
  import { computed, getCurrentInstance } from 'vue';
  import PosUiField from '@/components/pos-v2/ui/PosUiField.vue';
  import PosUiTextarea from '@/components/pos-v2/ui/PosUiTextarea.vue';
  import { useCategoryEditorContext } from '@/composition/catalog/use-category-editor';
  import {
    MAX_CATEGORY_DESCRIPTION,
    MAX_CATEGORY_NAME,
  } from '@/domain/catalog/category-form';
  import { nextDomId } from '@/utils/dom-id.utils';
  import V2CatalogEditorSection from '../shared/V2CatalogEditorSection.vue';

  /** «Lo básico» of a category (plan §5.15): name and description. */
  const editor = useCategoryEditorContext();
  const proxy = getCurrentInstance()!.proxy;
  const t = (key: string, params?: Record<string, unknown>) =>
    String(proxy.$t(key, params));
  const nameId = nextDomId('category-name');
  const descriptionId = nextDomId('category-description');

  const form = computed(() => editor.form.value);
  const readOnly = computed(() => editor.readOnly.value);

  const nameError = computed(() => {
    const error = editor.visibleErrors.value.name;
    if (error === 'required') return t('menuPage.editor.errors.name');
    if (error === 'tooLong')
      return t('menuPage.editor.errors.tooLong', { n: MAX_CATEGORY_NAME });
    return undefined;
  });
  const descriptionError = computed(() =>
    editor.visibleErrors.value.description
      ? t('menuPage.editor.errors.tooLong', { n: MAX_CATEGORY_DESCRIPTION })
      : undefined
  );
</script>

<template>
  <V2CatalogEditorSection
    v-if="form"
    class="v2-catalog-category-basics-section"
    :title="t('menuPage.editor.basics')"
  >
    <PosUiField
      :label="t('menuPage.editor.name')"
      :input-id="nameId"
      :error="nameError"
    >
      <PosUiTextarea
        :id="nameId"
        :value="form.name"
        :maxlength="MAX_CATEGORY_NAME"
        :auto-grow="{ minRows: 1, maxRows: 3 }"
        :disabled="readOnly"
        @input="editor.update('name', $event)"
      />
    </PosUiField>
    <PosUiField
      class="basics-description"
      :label="t('menuPage.editor.description')"
      :input-id="descriptionId"
      :error="descriptionError"
      :hint="t('menuPage.editor.categoryDescriptionHint')"
    >
      <PosUiTextarea
        :id="descriptionId"
        :value="form.description"
        :maxlength="MAX_CATEGORY_DESCRIPTION"
        :auto-grow="{ minRows: 2, maxRows: 6 }"
        :disabled="readOnly"
        @input="editor.update('description', $event)"
      />
    </PosUiField>
  </V2CatalogEditorSection>
</template>

<style scoped lang="scss">
  .v2-catalog-category-basics-section {
    .basics-description {
      @apply tw-mt-4;
    }
  }
</style>
